import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Save, Building2, Copy, Check, ImageIcon, ExternalLink, Trash2, Video } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { dealerAuth } from '../../services/dealerApi';
import { toast } from '../../hooks/use-toast';
import { compressForUpload } from '../../utils/imageCompress';

const inputCls = 'bg-slate-950/60 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus-visible:ring-0';
const labelCls = 'block text-xs uppercase tracking-wider text-slate-400 mb-2';

const TEMPLATES = [
  { id: 'clean_white', name: 'Clean White Modern', description: 'Minimal modern dealership style. (Default)' },
  { id: 'premium_dark', name: 'Premium Dark', description: 'Luxury style with large hero visuals.' },
  { id: 'luxury', name: 'Luxury', description: 'Warm ivory background with deep burgundy accents. Boutique showroom feel.' },
];

const FREE_IMAGE_SOURCES = [
  { name: 'Unsplash', url: 'https://unsplash.com/s/photos/luxury-car-showroom', note: 'Premium, editorial' },
  { name: 'Pexels', url: 'https://www.pexels.com/search/car%20showroom/', note: 'Wide, royalty-free' },
  { name: 'Pixabay', url: 'https://pixabay.com/images/search/car%20dealership/', note: 'No attribution needed' },
];

const DEFAULT_HERO_PREVIEW = 'https://images.unsplash.com/photo-1583489320541-03aa6b62987c?auto=format&fit=crop&w=900&q=60';

const DealerProfile = () => {
  const { dealer, refreshDealer } = useOutletContext();
  const [form, setForm] = useState({
    name: '', address: '', city: '', contact_number: '', whatsapp_number: '',
    year_of_inception: '', tagline: '', logo_url: '', hero_image_url: '', hero_video_url: '', template: 'clean_white',
    custom_domain: '',
    trust_finance_assistance: true,
    trust_exchange_assistance: true,
    trust_documentation_support: true,
    trust_test_drive_available: true,
  });
  const [saving, setSaving] = useState(false);
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroVideoUploading, setHeroVideoUploading] = useState(false);
  const [copied, setCopied] = useState(false);

  const siteUrl = dealer ? `${window.location.origin}/dealers/${dealer.slug}` : '';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      toast({ title: 'Link copied!', description: 'Paste it on your Google Business page.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Copy failed', description: 'Long-press the link to copy manually.', variant: 'destructive' });
    }
  };

  useEffect(() => {
    if (dealer) {
      setForm({
        name: dealer.name || '',
        address: dealer.address || '',
        city: dealer.city || '',
        contact_number: dealer.contact_number || '',
        whatsapp_number: dealer.whatsapp_number || '',
        year_of_inception: dealer.year_of_inception || '',
        tagline: dealer.tagline || '',
        logo_url: dealer.logo_url || '',
        hero_image_url: dealer.hero_image_url || '',
        hero_video_url: dealer.hero_video_url || '',
        template: (dealer.template === 'marketplace' ? 'luxury' : (dealer.template || 'clean_white')),
        custom_domain: dealer.custom_domain || '',
        trust_finance_assistance: dealer.trust_finance_assistance !== false,
        trust_exchange_assistance: dealer.trust_exchange_assistance !== false,
        trust_documentation_support: dealer.trust_documentation_support !== false,
        trust_test_drive_available: dealer.trust_test_drive_available !== false,
      });
    }
  }, [dealer]);

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressForUpload(file);
      const { dealerListings: dl } = await import('../../services/dealerApi');
      const res = await dl.uploadPhotos([compressed]);
      if (res.photos?.[0]?.url) setForm({ ...form, logo_url: res.photos[0].url });
    } catch (err) {
      toast({
        title: 'Logo upload failed',
        description: err?.response?.data?.detail || 'Please try a smaller image or check your connection.',
        variant: 'destructive',
      });
    }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Backend auto-compresses, so allow up to 25 MB raw input (typical phone photos)
    if (file.size > 25 * 1024 * 1024) {
      toast({ title: 'Image too large', description: 'Please upload under 25 MB.', variant: 'destructive' });
      return;
    }
    setHeroUploading(true);
    try {
      const compressed = await compressForUpload(file);
      const { dealerListings: dl } = await import('../../services/dealerApi');
      const res = await dl.uploadPhotos([compressed]);
      if (res.photos?.[0]?.url) {
        setForm({ ...form, hero_image_url: res.photos[0].url });
        toast({ title: 'Hero image uploaded', description: 'Click Save Profile to apply it to your site.' });
      }
    } catch (err) {
      toast({
        title: 'Hero upload failed',
        description: err?.response?.data?.detail || 'Please try a smaller image or check your connection.',
        variant: 'destructive',
      });
    } finally {
      setHeroUploading(false);
      e.target.value = '';
    }
  };

  const removeHero = () => setForm({ ...form, hero_image_url: '' });

  // ---- Hero VIDEO upload (uses GridFS-backed /api/dealers/hero-video) ----
  const handleHeroVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // 100 MB hard cap — matches backend MAX_VIDEO_BYTES.
    if (file.size > 100 * 1024 * 1024) {
      toast({ title: 'Video too large', description: 'Please upload a clip under 100 MB (trim it in your phone gallery).', variant: 'destructive' });
      e.target.value = '';
      return;
    }
    setHeroVideoUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = localStorage.getItem('dealer_token');
      const res = await fetch(`${apiBase}/api/dealers/hero-video`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      setForm((prev) => ({ ...prev, hero_video_url: data.hero_video_url }));
      toast({ title: 'Hero video uploaded', description: 'It will replace the hero image on your public site once you save the profile.' });
      refreshDealer && refreshDealer();
    } catch (err) {
      toast({ title: 'Hero video upload failed', description: err.message || 'Please try a smaller / shorter clip.', variant: 'destructive' });
    } finally {
      setHeroVideoUploading(false);
      e.target.value = '';
    }
  };

  const removeHeroVideo = async () => {
    try {
      const token = localStorage.getItem('dealer_token');
      await fetch(`${apiBase}/api/dealers/hero-video`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch { /* best-effort; UI still reflects removal below */ }
    setForm((prev) => ({ ...prev, hero_video_url: '' }));
    refreshDealer && refreshDealer();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year_of_inception: form.year_of_inception ? parseInt(form.year_of_inception) : null,
      };
      await dealerAuth.updateProfile(payload);
      toast({ title: 'Profile saved' });
      refreshDealer && refreshDealer();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const heroPreviewUrl = form.hero_image_url
    ? (form.hero_image_url.startsWith('http') ? form.hero_image_url : `${apiBase}/api${form.hero_image_url}`)
    : DEFAULT_HERO_PREVIEW;
  const heroIsDefault = !form.hero_image_url;

  return (
    <div data-testid="dealer-profile-page" className="space-y-6">
      <div>
        <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-medium">Profile</span>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-white tracking-tight flex items-center gap-3">
          <Building2 className="w-7 h-7 text-cyan-400" /> Dealer Profile
        </h1>
        <p className="mt-1 text-slate-400 text-sm">Complete your profile to publish your dealer site</p>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4">
          <h2 className="text-base font-semibold text-white">Business Info</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Dealer Name *</label>
              <Input data-testid="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Tagline</label>
              <Input data-testid="profile-tagline" placeholder="Premium pre-owned cars" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>City *</label>
              <Input data-testid="profile-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Year of Inception</label>
              <Input data-testid="profile-year" type="number" min="1990" max={new Date().getFullYear()} value={form.year_of_inception} onChange={(e) => setForm({ ...form, year_of_inception: e.target.value })} className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Address</label>
              <Textarea data-testid="profile-address" rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Contact Number</label>
              <Input data-testid="profile-contact" maxLength={15} value={form.contact_number} onChange={(e) => setForm({ ...form, contact_number: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>WhatsApp Number *</label>
              <Input data-testid="profile-whatsapp" required maxLength={15} value={form.whatsapp_number} onChange={(e) => setForm({ ...form, whatsapp_number: e.target.value })} className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Logo</label>
            <div className="flex items-center gap-4">
              {form.logo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/api${form.logo_url}`} alt="logo" className="w-16 h-16 rounded-xl object-cover bg-slate-800" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-slate-800 flex items-center justify-center text-slate-500 text-xs">No logo</div>
              )}
              <label className="cursor-pointer px-4 py-2 rounded-lg bg-slate-950/60 border border-slate-700 hover:border-cyan-500/40 text-sm text-slate-300">
                Upload Logo
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="profile-logo-input" />
              </label>
            </div>
          </div>
        </div>

        {/* Hero Image */}
        <div data-testid="hero-image-card" className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-cyan-400" /> Website Hero Image
              </h2>
              <p className="text-xs text-slate-400 mt-1 max-w-xl">
                The banner image at the top of your public site. We've set a clean default — replace it with a photo of your showroom or a hero car for a personal touch.
              </p>
            </div>
            {heroIsDefault && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-2.5 py-1">
                Using default
              </span>
            )}
          </div>

          {/* Preview */}
          <div className="relative rounded-xl overflow-hidden border border-slate-700/60 aspect-[16/7] bg-slate-950">
            {form.hero_video_url ? (
              <video
                src={form.hero_video_url.startsWith('http') ? form.hero_video_url : `${apiBase}${form.hero_video_url}`}
                className="w-full h-full object-cover"
                autoPlay
                loop
                muted
                playsInline
                data-testid="hero-preview-video"
              />
            ) : (
              <img
                src={heroPreviewUrl}
                alt="Hero preview"
                className="w-full h-full object-cover"
                data-testid="hero-preview-img"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
            <div className="absolute bottom-3 left-3 text-white">
              <div className="font-extrabold text-base sm:text-lg leading-none">{form.name || 'Your dealership'}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-300/80 mt-1">Live Preview</div>
            </div>
            {form.hero_video_url && (
              <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 bg-black/70 backdrop-blur text-[10px] font-mono uppercase tracking-[0.22em] text-white rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Video
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <label
              data-testid="hero-upload-btn"
              className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 hover:border-cyan-500/50 text-sm text-cyan-300 transition-colors ${heroUploading ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <ImageIcon className="w-4 h-4" />
              {heroUploading ? 'Uploading…' : (heroIsDefault ? 'Upload Hero Image' : 'Replace Hero Image')}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleHeroUpload}
                data-testid="hero-image-input"
              />
            </label>
            <label
              data-testid="hero-video-upload-btn"
              className={`cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-500/10 border border-purple-500/30 hover:border-purple-500/50 text-sm text-purple-300 transition-colors ${heroVideoUploading ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <Video className="w-4 h-4" />
              {heroVideoUploading ? 'Uploading…' : (form.hero_video_url ? 'Replace Hero Video' : 'Upload Hero Video')}
              <input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                className="hidden"
                onChange={handleHeroVideoUpload}
                data-testid="hero-video-input"
              />
            </label>
            {form.hero_video_url && (
              <button
                type="button"
                onClick={removeHeroVideo}
                data-testid="hero-video-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 hover:border-red-500/40 text-sm text-slate-300 hover:text-red-300 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Remove Video
              </button>
            )}
            {!heroIsDefault && !form.hero_video_url && (
              <button
                type="button"
                onClick={removeHero}
                data-testid="hero-remove-btn"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 hover:border-red-500/40 text-sm text-slate-300 hover:text-red-300 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> Use Default
              </button>
            )}
            <span className="text-[11px] text-slate-500">
              Image: 1920×800 JPG/PNG · &lt; 8 MB &nbsp;·&nbsp; Video: MP4/MOV · 10-30s · &lt; 100 MB · Video overrides image
            </span>
          </div>

          {/* Free image sources */}
          <div className="border-t border-slate-700/50 pt-4">
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-400 mb-2 font-mono">
              Don't have a photo? Download free, high-quality images here →
            </p>
            <div className="flex flex-wrap gap-2">
              {FREE_IMAGE_SOURCES.map((src) => (
                <a
                  key={src.name}
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`free-source-${src.name.toLowerCase()}`}
                  className="group inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-950/60 border border-slate-700 hover:border-cyan-500/40 hover:bg-slate-900 transition-colors text-sm"
                >
                  <span className="font-semibold text-white">{src.name}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-cyan-300">
                    {src.note}
                  </span>
                  <ExternalLink className="w-3 h-3 text-slate-500 group-hover:text-cyan-300" />
                </a>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              All three sources are 100% free for commercial use. Search "luxury car showroom", "supercar", or "automotive interior" for hero-worthy shots.
            </p>
          </div>
        </div>

        {/* Template Selection */}
        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-1">Buyer Trust Badges</h2>
          <p className="text-xs text-slate-400 mb-4">
            Shown on every car detail page under <em>Why Buy From This Dealer</em>. Turn off any you don&apos;t offer.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: 'trust_finance_assistance', label: 'Finance Assistance Available' },
              { key: 'trust_exchange_assistance', label: 'Exchange Assistance Available' },
              { key: 'trust_documentation_support', label: 'Documentation Support' },
              { key: 'trust_test_drive_available', label: 'Test Drive Available' },
            ].map((t) => {
              const on = !!form[t.key];
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setForm({ ...form, [t.key]: !on })}
                  data-testid={`trust-${t.key}`}
                  className={`text-left px-4 py-3 rounded-xl border-2 transition-colors flex items-center justify-between gap-3 ${
                    on
                      ? 'bg-cyan-500/10 border-cyan-500/50 text-white'
                      : 'bg-slate-950/40 border-slate-700 text-slate-400 hover:border-slate-600'
                  }`}
                >
                  <span className="text-sm font-medium">{t.label}</span>
                  <span className={`shrink-0 w-9 h-5 rounded-full relative transition-colors ${on ? 'bg-cyan-500' : 'bg-slate-700'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? 'left-4' : 'left-0.5'}`} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-4">Public Website Template</h2>
          <div className="grid sm:grid-cols-3 gap-3">
            {TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm({ ...form, template: t.id })}
                data-testid={`template-${t.id}`}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${
                  form.template === t.id
                    ? 'bg-cyan-500/10 border-cyan-500/50'
                    : 'bg-slate-950/40 border-slate-700 hover:border-slate-600'
                }`}
              >
                <div className="font-semibold text-white text-sm">{t.name}</div>
                <div className="mt-1 text-xs text-slate-400">{t.description}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-slate-900/40 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6">
          <h2 className="text-base font-semibold text-white mb-1">Custom Domain</h2>
          <p className="text-xs text-slate-400 mb-4">
            Point your own domain (e.g. <span className="text-slate-300">streetcars.in</span>) to this microsite.
            After saving, add a DNS record on your registrar (GoDaddy, Namecheap, etc.) pointing the domain to our platform — DNS setup is manual for now.
          </p>
          <label className={labelCls}>Domain</label>
          <Input
            data-testid="profile-custom-domain"
            placeholder="streetcars.in"
            value={form.custom_domain}
            onChange={(e) => setForm({ ...form, custom_domain: e.target.value })}
            className={inputCls}
          />
          <p className="mt-2 text-[11px] text-slate-500">
            Enter just the domain — no <code className="text-slate-400">https://</code> or <code className="text-slate-400">www.</code> prefix needed. We&apos;ll normalize it automatically.
          </p>
          {form.custom_domain && (
            <p className="mt-3 text-xs text-cyan-400" data-testid="custom-domain-preview">
              Once DNS is pointed here, your microsite will be live at <span className="font-mono">https://{form.custom_domain.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/$/, '')}</span>
            </p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">Site URL: <span className="text-cyan-400">/dealers/{dealer?.slug}</span></p>
          <Button type="submit" disabled={saving} data-testid="profile-save-btn"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-slate-950 font-bold rounded-xl px-6">
            {saving ? 'Saving...' : <><Save className="w-4 h-4 mr-2" /> Save Profile</>}
          </Button>
        </div>
      </form>

      {/* Shareable Site Link */}
      {dealer?.slug && (
        <div className="bg-gradient-to-br from-cyan-500/5 to-blue-500/5 backdrop-blur-xl border border-cyan-500/20 rounded-2xl p-5">
          <div className="flex items-start gap-3 mb-3">
            <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-4 h-4 text-cyan-400" />
            </div>
            <div>
              <h3 className="font-semibold text-white text-sm">Your Public Website</h3>
              <p className="text-xs text-slate-400 mt-0.5">Copy this link and paste it on your Google Business page, WhatsApp status, or social bios.</p>
            </div>
          </div>
          <div className="flex items-stretch gap-2">
            <div data-testid="dealer-site-url" className="flex-1 px-3 py-2.5 rounded-lg bg-slate-950/60 border border-slate-700 text-cyan-300 text-sm font-mono truncate select-all">
              {siteUrl}
            </div>
            <Button
              type="button"
              onClick={handleCopy}
              data-testid="copy-site-url-btn"
              className={`${copied ? 'bg-green-500 hover:bg-green-400 text-slate-950' : 'bg-cyan-500 hover:bg-cyan-400 text-slate-950'} font-semibold whitespace-nowrap`}
            >
              {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy Link</>}
            </Button>
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dealer-open-site-btn"
              className="inline-flex items-center px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-900/40 text-slate-300 hover:text-white hover:border-cyan-500/40 text-sm whitespace-nowrap"
            >
              Open
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default DealerProfile;
